/**
 * oj-carousel.js — infinite, full-bleed product carousel controls.
 *
 * Native CSS scroll-snap drives touch / trackpad. This component adds:
 *  - always-visible prev/next arrows (desktop),
 *  - seamless infinite looping: the slide set is tripled and the scroll
 *    position is kept in the middle band, so it never reaches a hard edge
 *    and wraps back to the start with no visible jump.
 *
 * Non-negotiables honoured:
 *  1. Vanilla Custom Element, no framework.
 *  2. Real work deferred until visible (IntersectionObserver).
 *  3. Full teardown in disconnectedCallback (editor re-renders aggressively).
 *  4. prefers-reduced-motion respected (instant scroll, no smooth animation).
 */

class OjCarousel extends HTMLElement {
  #observer = null;
  #abort = null;
  #initialised = false;
  #track = null;
  #prev = null;
  #next = null;
  #loop = false;
  #band = 0;
  #settleTimer = 0;
  #resizeTimer = 0;
  #lastWidth = 0;
  #onShopify = null;

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.#observer?.disconnect();
          this.#observer = null;
          this.#init();
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);

    // The Theme Editor re-renders aggressively. A section reload recreates the
    // element (connectedCallback re-runs), but a select does not — so on either
    // editor event, initialise if needed or re-measure (re-clone + recentre).
    // Both bubble to the document from the section that contains this element.
    this.#onShopify = (event) => {
      const t = event.target;
      if (!t || typeof t.contains !== 'function' || !t.contains(this)) return;
      if (this.#initialised) {
        this.#measure();
      } else {
        this.#init();
      }
    };
    document.addEventListener('shopify:section:load', this.#onShopify);
    document.addEventListener('shopify:section:select', this.#onShopify);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#onShopify) {
      document.removeEventListener('shopify:section:load', this.#onShopify);
      document.removeEventListener('shopify:section:select', this.#onShopify);
      this.#onShopify = null;
    }
    this.#abort?.abort();
    this.#abort = null;
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#resizeTimer);
    this.#initialised = false;
    this.#track = null;
    this.#prev = null;
    this.#next = null;
    this.#loop = false;
    this.#band = 0;
  }

  #init() {
    if (this.#initialised) return;
    this.#initialised = true;

    this.#track = this.querySelector('[data-oj-track]');
    this.#prev = this.querySelector('[data-oj-prev]');
    this.#next = this.querySelector('[data-oj-next]');
    if (!this.#track) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#prev?.addEventListener('click', () => this.#page(-1), { signal });
    this.#next?.addEventListener('click', () => this.#page(1), { signal });
    this.#track.addEventListener('scroll', () => this.#onScroll(), {
      signal,
      passive: true,
    });
    // Debounced, width-gated: iOS URL-bar show/hide fires resize with only a
    // height change, and re-cloning the whole track on every raw event thrashes
    // layout during a desktop drag-resize.
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(this.#resizeTimer);
        this.#resizeTimer = setTimeout(() => {
          if (this.#track && this.#track.clientWidth !== this.#lastWidth) this.#measure();
        }, 150);
      },
      { signal, passive: true }
    );

    // Defer to the next frame so layout is settled before measuring.
    requestAnimationFrame(() => this.#measure());
  }

  /** Decide whether looping applies, (re)build clones, recentre. */
  #measure() {
    if (!this.#track) return;
    this.#lastWidth = this.#track.clientWidth;

    // Remove any clones from a previous pass before re-measuring.
    this.#track.querySelectorAll('[data-oj-clone]').forEach((n) => n.remove());

    // scrollWidth (before cloning) is the reliable total content width.
    const overflows = this.#track.scrollWidth - this.#track.clientWidth > 1;

    this.#loop = overflows && this.#track.children.length > 0;
    this.toggleAttribute('data-oj-static', !this.#loop);

    if (!this.#loop) return;

    const originals = [...this.#track.children];

    // Triple the set: [originals][cloneA][cloneB] so there is a full set of
    // runway on each side of the middle band.
    for (let pass = 0; pass < 2; pass++) {
      originals.forEach((el) => {
        const clone = el.cloneNode(true);
        clone.setAttribute('data-oj-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, input').forEach((f) => {
          f.setAttribute('tabindex', '-1');
        });
        // Duplicate ids (quick-add forms, editor block attrs) make invalid HTML
        // and break label/form= association — clones are visual runway only.
        clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
        clone.querySelectorAll('[for]').forEach((n) => n.removeAttribute('for'));
        clone.querySelectorAll('[form]').forEach((n) => n.removeAttribute('form'));
        if (clone.hasAttribute('id')) clone.removeAttribute('id');
        this.#track.appendChild(clone);
      });
    }

    // Start flush at the first card of the middle copy. The band is the scroll
    // distance of exactly one original set — measured as the gap between the first
    // original and the first middle copy, NOT firstMiddle.offsetLeft on its own.
    // offsetLeft is relative to the positioned viewport, so it carries the page
    // gutter; using it raw made the band a non-multiple of the card pitch, so every
    // wrap drifted by the gutter width and the rail slowly slipped off the card
    // edges (half-card peeks after a few loops). Original→middle cancels that offset,
    // leaving band = exactly N card steps, so every wrap lands flush on a full card.
    const firstMiddle = this.#track.children[originals.length];
    const firstOriginal = this.#track.children[0];
    this.#band =
      firstMiddle && firstOriginal
        ? firstMiddle.offsetLeft - firstOriginal.offsetLeft
        : this.#track.scrollWidth / 3;
    this.#track.scrollLeft = this.#band;
  }

  #page(direction) {
    if (!this.#track) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Advance by a single card so each click moves one product.
    const item = this.#track.querySelector('.oj-pcar__item');
    const step = item ? item.getBoundingClientRect().width : this.#track.clientWidth;
    this.#track.scrollBy({
      left: step * direction,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }

  #onScroll() {
    // Recentre once scrolling has settled, so the wrap is never visible
    // mid-animation.
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#recentre(), 140);
  }

  #recentre() {
    if (!this.#loop || !this.#track) return;
    const band = this.#band || this.#track.scrollWidth / 3;
    if (band <= 0) return;
    const x = this.#track.scrollLeft;
    if (x >= band * 2) {
      this.#track.scrollLeft = x - band;
    } else if (x < band) {
      this.#track.scrollLeft = x + band;
    }
  }
}

if (!customElements.get('oj-carousel')) {
  customElements.define('oj-carousel', OjCarousel);
}
